import { Checkbox, Dialog, FormControlLabel, Popover, Tooltip } from "@mui/material";
import {
  ArrowLeft,
  Edit,
  Filter,
  PlusCircle,
  Trash,
  Trash2,
} from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import API from "../../API/newIndex";
import BulkEditPortfolio from "./BulkEditPortfolio";
import { DataLoad, PlanPermission, TableColumnsContext } from "../../App";
import { NOPHOTO_IMAGE } from "../../Assets/StaticData/StaticImage";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { formatDate } from "../../controllers/FormatAmount";
import { clientData } from "../../controllers/GetClient";
import MuiSwitch from "../../controllers/MuiSwitch";
import { getInitialPage, replacePageInUrl } from "../../controllers/PageHandleFromQuery";
import { parentNavigate } from "../../controllers/ParentNavigate";
import { useUser } from "../../controllers/UserContext";
import { useRoutingByType } from "../../controllers/useRouting";
import RenderCustomFieldValue from "../../utilities/RenderCustomFieldValue";
import { ButtonComponent, CommonTable, ConfirmationPopup, PageLimitDropDownComponent, PaginationComponent, SearchComponent, TableStatusDropdown } from '@shopeasy-dashboard-ui/ui-library';
import { isAscendingOrder } from "../FormInput/CommonTable";
import NoRecords from "../Main/NoRecords";
import Notification from "../Notification";
import * as defaultCustomFields from '../../utilities/defaultCustomTableField';
import useTableApi from "../../utilities/useTableApi";

function MyPortfolios() {
  const handleTableApi = useTableApi();
  const [Page, setPage] = useState(getInitialPage());
  const { dataLoading, setDataLoading } = useContext(DataLoad);
  const location = useLocation();
  const [clearRow, setClearRow] = useState(false);
  const initialLimit = localStorage.getItem("limit");
  const [limit, setLimit] = useState(initialLimit);
  const [search, setSearch] = useState(
    location?.state?.searchkey ? location?.state?.searchkey : "",
  );
  const [portfolioData, setPortfolioData] = useState();
  const { sortData, isUserLoaded } = useUser();
  const userContext = useUser();

  const savedSort = sortData?.portfolios || sortData?.portfolio;

  const [sortBy, setSortBy] = useState(
    () => (location?.state?.accending ? location?.state?.field : savedSort?.sort_by || 'createdAt')
  );
  const [orderBy, setOrderBy] = useState(() => {
    if (location?.state?.accending) {
      return { orderByValue: 'ASC', isDecending: false };
    }
    const isAsc = isAscendingOrder(savedSort?.order_by);
    return {
      orderByValue: savedSort?.order_by ? (isAsc ? 'ASC' : 'DESC') : 'DESC',
      isDecending: savedSort?.order_by ? !isAsc : true,
    };
  });
  const isInitialSortLoaded = useRef(Boolean(location?.state?.field || savedSort?.sort_by));
  const [openBulkEditModal, setOpenBulkEditModal] = useState(false);
  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const [allPortfolio, setAllPortfolio] = useState();
  const [publishPortfolio, setPublishPortfolio] = useState();
  const [draftPortfolio, setDraftPortfolio] = useState();
  const [openTab, setOpenTab] = useState(getQueryParams());
  const { setPlanPermission } = useContext(PlanPermission);
  const [portfolioId, setPortfolioId] = useState(null);
  const { tableColumnsData } = useContext(TableColumnsContext);

  const apiFieldNames = useMemo(() => {
    const dataObj = tableColumnsData?.data?.data || tableColumnsData?.data || tableColumnsData;
    if (Array.isArray(dataObj?.portfolios)) return dataObj.portfolios;
    if (Array.isArray(dataObj?.portfolio)) return dataObj.portfolio;
    if (Array.isArray(tableColumnsData?.portfolios)) return tableColumnsData.portfolios;
    if (Array.isArray(tableColumnsData?.portfolio)) return tableColumnsData.portfolio;
    return [];
  }, [tableColumnsData]);

  const dynamicColumns = useMemo(() => {
    const fixedKeys = new Set([
      '_id', '__v', 'client', 'tenant', 'store_client', 'store_tenant', 'user',
      'additional_images', 'images', 'image', 'title', 'portfoliocategory',
      'active', 'createdAt', 'updatedAt', 'comment_count', 'action', 'actions',
      'checkbox', 'selection', 'select', 'drag_handle', 'draghandle', 'drag',
      'portfolio_id', 'import_id', 'portfolio_slug_name', 'sort_order', 'is_draft',
    ]);

    return apiFieldNames
      .filter((fieldName) => typeof fieldName === 'string' && fieldName.trim() && !fixedKeys.has(fieldName))
      .map((fieldName) => {
        const isDotted = fieldName.includes('.');
        const parts = isDotted ? fieldName.split('.') : null;

        const label = fieldName
          .replace(/\./g, ' • ')
          .replace(/[-_]/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/\b\w/g, (c) => c.toUpperCase());

        const getValue = (row) => {
          if (!row) return undefined;
          if (isDotted && parts) {
            return parts.reduce((obj, k) => (obj != null ? obj[k] : undefined), row);
          }
          return row[fieldName];
        };

        const isImageCol =
          fieldName.toLowerCase().includes('image') ||
          fieldName.toLowerCase().includes('photo') ||
          fieldName.toLowerCase().includes('img') ||
          fieldName.toLowerCase().includes('thumbnail') ||
          fieldName.toLowerCase().includes('logo') ||
          fieldName.toLowerCase().includes('banner') ||
          fieldName.toLowerCase().includes('icon');

        return {
          key: fieldName,
          title: label,
          width: isImageCol ? '80px' : '160px',
          minWidth: isImageCol ? '70px' : '110px',
          maxWidth: isImageCol ? '95px' : undefined,
          responsiveWidth: isImageCol ? '70px' : '100px',
          align: isImageCol ? 'text-center' : undefined,
          headerClassName: isImageCol
            ? 'thead-image px-2 text-center whitespace-nowrap'
            : 'px-3 whitespace-nowrap truncate',
          cellClassName: isImageCol
            ? 'table-image px-2 text-center'
            : 'px-3 truncate',
          render: (row) => {
            const val = getValue(row);
            return <RenderCustomFieldValue val={val} fieldName={fieldName} />;
          },
        };
      });
  }, [apiFieldNames]);

  const portfolioRoute = useRoutingByType("portfolio");
  const protocol = window.location.protocol + "//";

  useEffect(() => {
    setSearch(location?.state?.searchkey ? location?.state?.searchkey : "");
  }, [location?.state?.searchkey]);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    replacePageInUrl(Page);
  }, [Page]);

  useEffect(() => {
    if (!isInitialSortLoaded.current && isUserLoaded) {
      isInitialSortLoaded.current = true;
      if (savedSort && savedSort.sort_by) {
        const isAsc = isAscendingOrder(savedSort.order_by);
        const targetOrderBy = isAsc ? "ASC" : "DESC";
        if (savedSort.sort_by !== sortBy || targetOrderBy !== orderBy.orderByValue) {
          setSortBy(savedSort.sort_by);
          setOrderBy({
            orderByValue: targetOrderBy,
            isDecending: !isAsc,
          });
        }
      }
    }
  }, [savedSort, isUserLoaded, sortBy, orderBy.orderByValue]);

  // Sorting Data
  const SortData = (sort_by, explicitOrder) => {
    setSortBy(sort_by);
    let nextOrder = "DESC";
    if (explicitOrder !== undefined) {
      nextOrder = isAscendingOrder(explicitOrder) ? "ASC" : "DESC";
    } else if (sortBy === sort_by) {
      const isCurrentlyAsc = isAscendingOrder(orderBy);
      nextOrder = isCurrentlyAsc ? "DESC" : "ASC";
    } else {
      nextOrder = "DESC";
    }
    setOrderBy({
      orderByValue: nextOrder,
      isDecending: nextOrder === "DESC",
    });
  };

  var startRecord = (portfolioData?.data?.currentPage - 1) * limit + 1;
  var endRecord = Math.min(portfolioData?.data?.currentPage * limit, portfolioData?.data?.totalRecords);

  const debounceTimeout = useRef(null);

  useEffect(() => {
    // If user info is still loading on initial mount and route state did not specify sort, wait for user info so we fetch with the saved sort!
    if (!isUserLoaded && !isInitialSortLoaded.current && !location?.state?.field) {
      return;
    }

    const trimmed = search?.trim() || "";

    clearTimeout(debounceTimeout.current);

    if (trimmed === "") {
      GetPortfolios();
      setClearRow((prev) => !prev);
    } else if (trimmed.length >= 3) {
      debounceTimeout.current = setTimeout(() => {
        GetPortfolios();
        setClearRow((prev) => !prev);
      }, 500);
    }

    return () => clearTimeout(debounceTimeout.current);
  }, [Page, limit, openTab, search, sortBy, orderBy, isUserLoaded]);

  const GetPortfolios = async () => {
    setDataLoading(true);
    try {
      const data = await API({
        url: `/admin/portfolio/getPortfolios?page=${Page}&limit=${limit}&searchkey=${search?.length > 2 ? search : ""
          }&sort_by=${sortBy}&order_by=${orderBy.orderByValue}${openTab === "draft" ? "&is_draft=true" : openTab === "publish" ? "&is_draft=false" : ""
          }`,
        method: "get",
      });
      if (data.status === 200 || data.status === 304) {
        const result = data?.data;
        const totalRecords = result?.data?.totalRecords || 0;
        const formattedRecords = totalRecords.toString().padStart(2, "0");
        setPortfolioData(result);
        if (openTab === "all") {
          setAllPortfolio(formattedRecords);
          setPublishPortfolio(result?.data?.publishCount.toString().padStart(2, "0"));
          setDraftPortfolio(result?.data?.draftCount.toString().padStart(2, "0"));
        }
      }
    } catch (error) {
      if (error?.response?.status === 882) {
        setPlanPermission({ isPlanPermissions: false, forModule: "portfolio" });
      }
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    } finally {
      setDataLoading(false);
    }
  };

  const isLoadRender = useRef(true);
  useEffect(() => {
    if (isLoadRender.current) {
      isLoadRender.current = false;
      return;
    }
    setPage(1);
  }, [openTab]);

  const handlePublishPortfolio = async (id, value) => {
    try {
      const payload = {
        published: value,
      };
      await API({ url: `/admin/portfolio/updatePortfolio/${id}`, method: "put", data: payload });
      GetPortfolios();
      setIsNotification(true);
      setNotificationMsg(
        value === true ? "Portfolio is set to active" : "Portfolio is set to hidden",
      );
      setSeverity("success");
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    }
  };

  const [selectedRowsId, setSelectedRowsId] = useState([]);

  const EditPortfolio = (elem) => {
    parentNavigate(`/portfolio/post/${elem?.portfolio_slug_name}`);
  };

  const [confirmationPopup, setConfirmationPopup] = useState(false);
  const RemovePortfolio = async () => {
    try {
      const payload = {
        id: selectedRowsId,
      };
      const data = await API({ url: `/admin/portfolio/deletePortfolio`, method: "post", data: payload });
      if (data.status === 200 || data.status === 304) {
        setIsNotification(true);
        setNotificationMsg("Removed Successfully");
        setSeverity("success");
        setSelectedRowsId([]);
        setClearRow(!clearRow);
        GetPortfolios();
      }
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    }
    setConfirmationPopup(false);
  };

  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };
  const handleCloseConfirm = (value) => {
    setConfirmationPopup(value);
  };
  useEffect(() => {
    localStorage.setItem("limit", limit);
  }, [limit]);

  const [filter, setFilter] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleSelectRow = (e, id) => {
    if (e?.nativeEvent?.shiftKey) {
      const prevIndex = portfolioData?.data?.records?.findIndex(
        (x) => x._id === selectedRowsId[selectedRowsId?.length - 1],
      );
      const currIndex = portfolioData?.data?.records?.findIndex((x) => x._id === id);
      const startIndex = Math.min(prevIndex, currIndex);
      const endIndex = Math.max(prevIndex, currIndex);
      const final = portfolioData?.data?.records?.slice(startIndex, endIndex + 1);

      if (selectedRowsId?.includes(id)) {
        setSelectedRowsId((prev) => [
          ...new Set(prev.filter((x) => !final.find((item) => item._id === x))),
        ]);
      } else {
        setSelectedRowsId((prev) => [...new Set([...prev, ...final.map((item) => item._id)])]);
      }
    } else if (id === "all") {
      if (selectedRowsId?.length > 0) {
        setSelectedRowsId([]);
      } else {
        setSelectedRowsId(portfolioData?.data?.records?.map((x) => x?._id));
      }
    } else {
      const existId = selectedRowsId?.includes(id);
      if (!existId) {
        setSelectedRowsId((prev) => [...prev, id]);
      } else {
        setSelectedRowsId((prev) => prev.filter((x) => x !== id));
      }
    }
  };

  const updateQueryParams = (params) => {
    const currentUrl = new URLSearchParams(window.location.search);
    currentUrl.set("type", params);
    window.history.pushState({}, "", window.location.pathname + "?" + currentUrl.toString());
    parentNavigate(window.location.pathname + "?" + currentUrl.toString());
  };
  function getQueryParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const initial = urlParams.get("type");
    return initial ? initial : "all";
  }
  useEffect(() => {
    updateQueryParams(openTab);
  }, [openTab]);

  const resetFilter = (e) => {
    e.preventDefault();
    setFilter(false);
    setAnchorEl(null);
    setSearch(location?.state?.searchkey ? location?.state?.searchkey : "");
    setPage(1);
    setLimit(limit === "all" ? 10 : limit);
  };
  const limitValue = limit === "all" ? portfolioData?.data?.totalRecords : Number(limit || 10);
  const startRec = portfolioData?.data?.currentPage ? (portfolioData.data.currentPage - 1) * limitValue + 1 : 0;
  const endRec = portfolioData?.data?.currentPage ? Math.min(
    portfolioData.data.currentPage * limitValue,
    portfolioData.data.totalRecords
  ) : 0;

  const columns = [
    {
      key: "checkbox",
      title: (
        <Checkbox
          checked={
            portfolioData?.data?.records?.length > 0 &&
            selectedRowsId?.length === portfolioData?.data?.records?.length
          }
          indeterminate={
            selectedRowsId?.length > 0 &&
            selectedRowsId?.length < portfolioData?.data?.records?.length
          }
          onChange={(e) => handleSelectRow(e, "all")}
        />
      ),
      align: "text-center",
      ignoreRowClick: true,
      width: "50px",
      minWidth: "50px",
      maxWidth: "50px",
      headerClassName: "!px-0 w-[50px] min-w-[50px] max-w-[50px] text-center",
      cellClassName: "!px-0 w-[50px] min-w-[50px] max-w-[50px] text-center",
      render: (row) => (
        <Checkbox
          checked={selectedRowsId?.length > 0 && selectedRowsId?.includes(row._id)}
          onChange={(e) => handleSelectRow(e, row._id)}
        />
      ),
    },
    {
      key: "image",
      title: t("lang_Image") || "Image",
      width: "75px",
      minWidth: "70px",
      maxWidth: "85px",
      responsiveWidth: "60px",
      headerClassName: "thead-image px-2 text-center",
      cellClassName: "table-image px-2 text-center",
      render: (row) => (
        <img
          width={40}
          height={50}
          onError={(e) => {
            e.target.src = NOPHOTO_IMAGE;
          }}
          src={row.image || NOPHOTO_IMAGE}
          className="p-0.5 max-h-full rounded-md mx-auto block"
          alt={row?.title}
        />
      ),
    },
    {
      key: "title",
      title: t("lang_Portfolio_Title") || "Portfolio Title",
      width: "500px",
      maxWidth: "550px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => (
        <p className="line-clamp-2 text-xs text-[var(--primary-text-color)] font-medium">
          {row.title || "-"}
        </p>
      ),
    },
    {
      key: "portfoliocategory",
      title: t("lang_Portfolio_Category_Name") || "Portfolio Category Name",
      width: "500px",
      maxWidth: "550px",
      headerClassName: "px-3 overflow-hidden truncate",
      cellClassName: "px-3 overflow-hidden truncate",
      render: (row) => (
        <div className="flex flex-wrap items-center gap-1 py-1 overflow-hidden max-w-full">
          {Array.isArray(row?.portfoliocategory) ? (
            row.portfoliocategory.map((item, idx) => (
              <span
                key={idx}
                className="bg-[var(--focus-tab-color)] border border-[var(--border-color)] inline-flex items-center whitespace-nowrap text-[12px] px-2.5 py-0.5 rounded-[6px] font-medium text-[var(--primary-text-color)]"
              >
                {item?.portfoliocategory_name || item?.parent_category || item}
              </span>
            ))
          ) : row?.portfoliocategory ? (
            <span className="bg-[var(--focus-tab-color)] border border-[var(--border-color)] inline-flex items-center whitespace-nowrap text-[12px] px-2.5 py-0.5 rounded-[6px] font-medium text-[var(--primary-text-color)]">
              {row?.portfoliocategory?.portfoliocategory_name || row?.portfoliocategory?.parent_category || String(row?.portfoliocategory)}
            </span>
          ) : (
            "-"
          )}
        </div>
      ),
    },
    {
      key: "active",
      title: openTab === "publish" ? t("lang_active") || "Active" : "",
      width: "120px",
      minWidth: "100px",
      headerClassName: "px-3 text-right whitespace-nowrap",
      cellClassName: "px-3 text-right whitespace-nowrap",
      align: "text-right",
      ignoreRowClick: true,
      render: (row) => (
        <>
          {openTab === "publish" && (
            row?.portfolio_publish_date && new Date(row?.portfolio_publish_date) >= new Date() ? (
              <div>
                <span className="block table-text text-right mb-0.5 font-medium">
                  {t("lang_active")}
                </span>
                <p className="flex table-text text-xs items-center justify-end">
                  {formatDate(row?.portfolio_publish_date)}
                </p>
              </div>
            ) : (
              <label className="relative flex justify-end w-full">
                <FormControlLabel
                  checked={row.published === true}
                  control={<MuiSwitch />}
                  onChange={() => handlePublishPortfolio(row?._id, !row?.published)}
                />
              </label>
            )
          )}
        </>
      ),
    },
    {
      key: "createdAt",
      title: t("lang_created_date") || "Created Date",
      width: "150px",
      minWidth: "140px",
      headerClassName: "px-3 whitespace-nowrap",
      cellClassName: "px-3 whitespace-nowrap",
      render: (row) => (
        <span className="table-text text-xs whitespace-nowrap">
          {row?.createdAt ? formatDate(row.createdAt) : "-"}
        </span>
      ),
    },
    {
      key: "comment_count",
      title: t("lang_Comment_Count") || "Comment Count",
      width: "140px",
      maxWidth: "150px",
      headerClassName: "px-3 whitespace-nowrap",
      cellClassName: "px-3 whitespace-nowrap",
      render: (row) => (
        <span className="text-xs font-semibold text-[var(--primary-text-color)]">
          {row?.comment_count ?? 0}
        </span>
      ),
    },
    ...dynamicColumns,
    {
      key: "action",
      title: t("lang_action"),
      width: "100px",
      maxWidth: "110px",
      responsiveWidth: "110px",
      headerClassName: "px-3 text-right",
      cellClassName: "px-3 text-right",
      align: "text-right",
      ignoreRowClick: true,
      render: (row) => (
        <div className="flex items-center space-x-2.5 justify-end w-full">
          <div className="text-xs font-thin hover:text-gray-900">
            <Tooltip title="Preview" arrow>
              <a
                href={
                  row?.portfolio_url ||
                  `${protocol}${clientData?.custom_domain || clientData?.customer_domain
                  }${portfolioRoute}/${row?.portfolio_slug_name}`
                }
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-external-link cursor-pointer text-green-600 transition"
                >
                  <g>
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </g>
                </svg>
              </a>
            </Tooltip>
          </div>
          <Tooltip title="Edit" arrow>
            <button onClick={() => EditPortfolio(row)}>
              <Edit className="w-[16px] h-[16px] text-[var(--table-text-color)] hover:text-[var(--primary-text-color)] transition-colors" />
            </button>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <button
              onClick={(e) => {
                e.preventDefault();
                setConfirmationPopup(true);
                setSelectedRowsId([row?._id]);
              }}
            >
              <Trash className="w-[16px] h-[16px] text-red-500 hover:text-red-600 transition-colors" />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const tableHeaderFilter = (manageFieldsBtn) => {
    return (dataLoading || (portfolioData?.data?.totalRecords && portfolioData?.data?.totalRecords > 0) || Number(allPortfolio) > 0 || search) ? (
      <div className="relative w-full border-b main-border-color secondary-bg-color px-3 py-2 flex flex-col pe-3 min-h-[50px]">
        <div className="flex flex-wrap items-center justify-between w-full gap-2 min-h-[38px]">
          <div className="flex items-center">
            <TableStatusDropdown
              value={openTab}
              onChange={(val) => {
                setOpenTab(val);
                updateQueryParams(val);
                setPage(1);
              }}
              options={[
                { label: t("lang_all"), value: "all", count: allPortfolio },
                ...(Number(publishPortfolio) > 0 ? [{ label: t("lang_published"), value: "publish", count: publishPortfolio }] : []),
                ...(Number(draftPortfolio) > 0 ? [{ label: t("lang_draft"), value: "draft", count: draftPortfolio }] : []),
              ]}
              placeholder={t("lang_all")}
            />
          </div>

          <div className="hidden md:flex items-center gap-2 ml-auto">
            <div className="w-[180px] md:w-[200px]">
              <SearchComponent
                placeholder="Search Portfolios"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                onClear={() => {
                  setSearch("");
                  setPage(1);
                }}
                isClearable={true}
                size="sm"
                iconPlacement="left"
              />
            </div>

            <div className="w-[80px] md:w-[100px] shrink-0 select-sm-height select-hide-label select-no-padding-y">
              <PageLimitDropDownComponent
                limit={limit}
                setLimit={setLimit}
                setPage={setPage}
                totalRecords={portfolioData?.data?.totalRecords || 0}
              />
            </div>
            {manageFieldsBtn}
          </div>

          <Tooltip title={t("lang_filter") || "Filter"} arrow>
            <button
              type="button"
              onClick={(e) => {
                setFilter(true);
                setAnchorEl(e.currentTarget);
              }}
              className="block md:hidden relative flex items-center justify-center p-2 border main-border-color rounded-lg bg-[var(--secondary-bg-color)] hover:bg-gray-50/50 transition-colors w-8 h-8 shrink-0"
            >
              <Filter className="h-4 w-4 text-[var(--table-text-color)]" />
            </button>
          </Tooltip>
        </div>

        {portfolioData?.data?.totalRecords > 0 && selectedRowsId?.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 mt-1 w-full">
            <span className="text-sm px-2 whitespace-nowrap">
              {selectedRowsId?.length} {t("lang_selected")}
            </span>
            <span className="me-1">|</span>
            <ButtonComponent
              variant="outline"
              size="sm"
              className="mx-1"
              onClick={() => setOpenBulkEditModal(true)}
              icon={<Edit className="w-3.5 h-3.5" />}
            >
              {t("lang_bulk_edit", "Bulk Edit")}
            </ButtonComponent>
            <ButtonComponent
              variant="danger_outline"
              size="sm"
              icon={<Trash2 className="w-3.5 h-3.5" />}
              onClick={(e) => {
                e.preventDefault();
                setConfirmationPopup(true);
              }}
            >
              {t("lang_delete_selection")}
            </ButtonComponent>
          </div>
        )}
      </div>
    ) : null;
  };

  return (
    <>
      <ConfirmationPopup
        heading="Delete Portfolio Post"
        subheading="Do you really want to delete this portfolio post from your store?"
        confirmationPopup={confirmationPopup}
        handleCloseConfirm={handleCloseConfirm}
        runFunction={RemovePortfolio}
      />
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      <div className="h-auto overflow-y-auto overflow-x-hidden pb-[64px] p-5">
        <div className="flex items-end justify-between page-header-pb">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/dashboard");
              }}
              className="back-arrow -ml-3.5"
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: t("lang_dashboard"), path: `/dashboard` },
                  { name: t("lang_Portfolio_Post"), path: "/portfolio/post" },
                ]}
              />
              <h4 className="text-xl md:text-2xl font-semibold text-primary">
                {t("lang_Portfolio_Post")}
              </h4>
            </div>
          </div>
          <div className="flex items-center">
            <ButtonComponent
              variant='primary'
              icon={<PlusCircle className='w-4 h-4' />}
              onClick={() => parentNavigate("/portfolio/post/add")}
            >
              {t("lang_Add_New_Portfolio_Post")}
            </ButtonComponent>
          </div>
        </div>
        <div className="flex justify-between w-full space-x-4">
          <div className="w-full space-y-3">
            <div className="flex w-full h-full overflow-hidden relative min-w-0">
              <div className="w-full">
                <div className="w-full transition-all duration-100 mb-[10px]">
                  <CommonTable
                    tableId="portfolios"
                    columns={columns}
                    data={portfolioData?.data?.records}
                    tableHeader={tableHeaderFilter}
                    tableFooter={
                      portfolioData?.data && portfolioData?.data?.totalRecords > 0 ? (
                        <PaginationComponent
                          count={portfolioData?.data?.totalPages || 1}
                          page={Page}
                          onChange={(e, v) => setPage(v)}
                          totalRecords={portfolioData?.data?.totalRecords}
                          startRecord={startRec}
                          endRecord={endRec}
                          limit={limit}
                          recordsLabel="Records"
                        />
                      ) : null
                    }
                    loading={dataLoading}
                    emptyMessage={<NoRecords />}
                    rowClick={(row) => EditPortfolio(row)}
                    onSort={SortData}
                    sortBy={sortBy}
                    orderBy={orderBy}
                    selectedRowsId={selectedRowsId}
                    setSelectedRowsId={setSelectedRowsId}
                    NEWAPI={handleTableApi}
                    tableDefaults={defaultCustomFields?.defaultPortfolioField}
                    tableWiseAllowedSortKeys={defaultCustomFields?.PORTFOLIO_SORT_FIELD}
                    userState={userContext}
                  />
                </div>
              </div>
              <Popover
                open={filter}
                anchorEl={anchorEl}
                onClose={() => {
                  setFilter(false);
                  setAnchorEl(null);
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  style: { marginTop: "8px", minWidth: "300px", borderRadius: "8px" },
                }}
                className="rounded-[6px]"
              >
                <div className="p-[20px] filter secondary-bg-color">
                  <div>
                    <div className="flex justify-between items-center">
                      <p className="flex font-semibold uppercase text-sm m-0">
                        <Filter className="me-1 w-4 h-4" />
                        {t("lang_filter")}
                      </p>
                      <button
                        type="button"
                        onClick={(e) => resetFilter(e)}
                        className="text-white bg-gray-800 hover:bg-gray-900 focus:outline-none font-medium rounded-md text-sm px-5 py-2 me-1 mb-2 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700 dark:border-gray-700"
                      >
                        Reset Filter
                      </button>
                    </div>
                  </div>
                  <hr className="main-border-color my-3"></hr>
                  <div className="grid gap-y-5 table-text">
                    <div>
                      <span className="text-sm capitalize font-medium mb-1 inline-block">
                        {t("lang_Search")}
                      </span>
                      <SearchComponent
                        placeholder="Search Portfolios"
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setPage(1);
                        }}
                        onClear={() => {
                          setSearch("");
                          setPage(1);
                        }}
                        iconPlacement="left"
                        isClearable={true}
                      />
                    </div>
                    <div>
                      <span className="text-sm table-text font-medium mb-1 inline-block">
                        {t("lang_set_items_limit")}
                      </span>
                      <PageLimitDropDownComponent
                        limit={limit}
                        setLimit={setLimit}
                        setPage={setPage}
                        totalRecords={portfolioData?.data?.totalRecords || 0}
                      />
                    </div>
                  </div>
                </div>
              </Popover>
            </div>
          </div>
        </div>
      </div>

      {openBulkEditModal && (
        <Dialog
          open={openBulkEditModal}
          disableEscapeKeyDown={true}
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              return;
            }
            setOpenBulkEditModal(false);
          }}
          fullScreen
          sx={{
            zIndex: 999999,
            "& .MuiDialog-paper": {
              zIndex: 999999,
              margin: 0,
              width: "100vw",
              height: "100vh",
              maxWidth: "100vw",
              maxHeight: "100vh",
              borderRadius: 0,
            },
          }}
        >
          <BulkEditPortfolio
            isBulkEdit={true}
            bulkPortfolioIds={selectedRowsId}
            onClose={() => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
            }}
            onSuccess={(updatedCount) => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
              setIsNotification(true);
              const count = typeof updatedCount === "number" ? updatedCount : 1;
              setNotificationMsg(
                `${count} portfolio${count > 1 ? "s" : ""} updated successfully!`
              );
              setSeverity("success");
              GetPortfolios();
            }}
          />
        </Dialog>
      )}
    </>
  );
}

export default MyPortfolios;
